import * as signalR from '@microsoft/signalr';

let notificationConnection: signalR.HubConnection | null = null;
let subscribedHousePointsOrgIds = new Set<string>();
let pendingHousePointsOrgIds = new Set<string>();
// Desired groups persist across reconnects/restarts; subscribed is per-connection.
let desiredHousePointsOrgIds = new Set<string>();
let globalOnHousePoints: ((payload: any) => void) | null = null;

async function flushPendingHousePointsSubs() {
  if (!notificationConnection) return;
  if (notificationConnection.state !== signalR.HubConnectionState.Connected) return;

  const pending = Array.from(pendingHousePointsOrgIds);
  if (pending.length === 0) return;

  for (const id of pending) {
    if (!subscribedHousePointsOrgIds.has(id)) {
      try {
        await notificationConnection.invoke('SubscribeHousePoints', id);
        subscribedHousePointsOrgIds.add(id);
      } catch (err) {
        console.error('[SignalR] Error subscribing to house points group:', id, err);
        // Don't remove from pending so we can retry later
        continue;
      }
    }
    pendingHousePointsOrgIds.delete(id);
  }
}

const stableHousePointsHandler = (payload: any) => {
  try {
    globalOnHousePoints?.(payload);
  } catch (e) {
    console.warn('House points handler error', e);
  }
  try {
    window.dispatchEvent(new CustomEvent('mascotPointsUpdated', { detail: payload }));
  } catch (e) {
    console.warn('Failed to dispatch mascotPointsUpdated event', e);
  }
};

// Vite uses import.meta.env; process.env may be undefined in Vite builds
const envApiUrl =
  (import.meta as any).env?.VITE_API_URL ??
  (import.meta as any).env?.VITE_API_BASE_URL ??
  (process as any).env?.VITE_API_URL ??
  (process as any).env?.API_URL;

// SSR-safe fallback function (API typically on :5001)
const getFallbackApiUrl = () => {
  if (typeof window === 'undefined') {
    return 'http://localhost:5001';
  }
  return `${window.location.protocol}//${window.location.hostname}:5001`;
};

const apiUrl = (envApiUrl || getFallbackApiUrl()).replace(/\/+$/, '');
const hubUrl = `${apiUrl}/hubs/notification`;

// Simple in-memory de-dupe for bursty hub events (same pass approved twice quickly)
const recentEventTimestamps = new Map<string, number>();
function shouldDedup(key: string, windowMs = 3000) {
  const now = Date.now();
  const last = recentEventTimestamps.get(key) ?? 0;
  if (now - last < windowMs) return true;
  recentEventTimestamps.set(key, now);
  return false;
}

export type OrderNotification =
  | { type: 'new'; orderId: number }
  | { type: 'cancel'; orderId: number }
  | { type: 'ready'; orderId: number; pickupCode: string; pickupLocation: string };

export async function startNotificationConnection(
  onHallPass?: (hp: any) => void,
  context: 'STUDENT' | 'nonStudent' = 'nonStudent',
  onOrder?: (payload: OrderNotification) => void,
  userId?: string,
  onMascotPoints?: (payload: any) => void,
  housePointsRootOrgId?: string | string[],
  housePointsReplaceExisting = false
) {
  // Keep the latest non-null points callback globally so other callers can't wipe it.
  if (onMascotPoints) {
    globalOnHousePoints = onMascotPoints;
  }

  const HP_DEBUG = false;
  if (!notificationConnection) {
    notificationConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();
    // Optional no-op handler to avoid server->client warnings if emitted
    notificationConnection.on("SubscribeHousePointsSuccess", () => {});
    notificationConnection.onreconnected(async (connectionId) => {
      void connectionId;
      // Re-subscribe to all desired groups on the new connection
      subscribedHousePointsOrgIds.clear();
      Array.from(desiredHousePointsOrgIds).forEach(id => pendingHousePointsOrgIds.add(id));
      // Add small delay to ensure connection is fully ready
      await new Promise(resolve => setTimeout(resolve, 100));
      await flushPendingHousePointsSubs();
    });
    notificationConnection.onclose((error) => {
      console.error('SignalR connection closed with error:', error);
      // When onclose fires, automatic reconnect has stopped. Any group memberships are lost.
      subscribedHousePointsOrgIds.clear();
      Array.from(desiredHousePointsOrgIds).forEach(id => pendingHousePointsOrgIds.add(id));
      setTimeout(() => {
        startNotificationConnection(
          onHallPass,
          context,
          onOrder,
          userId,
          globalOnHousePoints ?? onMascotPoints,
          Array.from(desiredHousePointsOrgIds)
        );
      }, 5000);
    });

    // Register these ONCE (do not re-register on later calls)
    notificationConnection.off("HousePointsUpdated", stableHousePointsHandler);
    notificationConnection.on("HousePointsUpdated", stableHousePointsHandler);
  } else {
    // Clear any previously registered handlers
    notificationConnection.off("ReceiveHallPass");
    notificationConnection.off("ApprovedHallPass");
    notificationConnection.off("DeniedHallPass");
    notificationConnection.off("SummonsHallPass");
    notificationConnection.off("ReturnHallPass");
    notificationConnection.off("ExpiredHallPass");
    notificationConnection.off("CancelledHallPass");
    notificationConnection.off("OrderPostAcornOrder");
    notificationConnection.off("OrderCancel");
    notificationConnection.off("OrderMarkReady");
    // IMPORTANT: do NOT clear HousePointsUpdated / MascotRedeemAcorn.
    // Those are registered once and dispatch via globalOnHousePoints.

    // Defensive: ensure the stable house points handlers are always wired.
    // This protects you even if some route accidentally called .off() earlier.
    notificationConnection.off("HousePointsUpdated", stableHousePointsHandler);
    notificationConnection.on("HousePointsUpdated", stableHousePointsHandler);
  }

  // Register event handlers based on context
  if (context !== "STUDENT") {
    // Helper to validate hall pass payload completeness (teacher context)
    const validateHallPassPayload = (label: string, hp: any) => {
      if (!HP_DEBUG || !hp || typeof hp !== 'object') return;
      // Minimum fields the frontend needs to correctly render without fallbacks
      const requiredFields = [
        'hallPassId',              // unique id
        'hallPassTypeId',          // to resolve description/duration
        'studentId',               // link to student
        'studentName',             // display name
        'hallPassStatusId',        // numeric status enum
        'status',                  // textual status (optional but helpful)
        'requestDecisionedAt',     // canonical start time (or requestAt/createdAt)
        'durationMinutes',         // explicit duration
      ];
      const present = Object.keys(hp);
      const missing = requiredFields.filter(f => hp[f] === undefined || hp[f] === null);
      if (missing.length) {
        console.warn(`[HP][WARN][${label}] Missing expected field(s): ${missing.join(', ')}. Present keys:`, present, 'Raw payload:', hp);
      }
      // Specific check: if both hallPassTypeId and any destination/location fields absent
      const hasType = hp.hallPassTypeId != null;
      const hasLocationName = hp.destinationLocationName || hp.location || hp?.destinationLocation?.locationName || hp?.explicitDestinationLocation?.locationName;
      if (!hasType && !hasLocationName) {
        console.warn(`[HP][WARN][${label}] Cannot derive destination/description because hallPassTypeId and destination location fields are all missing.`);
      }
    };
    notificationConnection.on("ReceiveHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ReceiveHallPass]', hp);
      validateHallPassPayload('ReceiveHallPass', hp);

      // Normalize common shape for downstream consumers
      const normalized = (() => {
        const n: any = { ...hp };

        // prefer explicit then type-provided path
        n.explicitPath = n.explicitPath ?? n.ExplicitPath ?? null;
        n.hallPassTypePath = n.hallPassTypePath ?? n.HallPassTypePath ?? null;

        n.startLocation = n.startLocation ?? n.StartLocation ?? n.explicitStartLocation ?? n.ExplicitStartLocation ?? null;
        n.destinationLocation = n.destinationLocation ?? n.DestinationLocation ?? n.explicitDestinationLocation ?? n.ExplicitDestinationLocation ?? null;

        // unify id/time/duration fields
        n.hallPassId = n.hallPassId ?? n.id ?? n.Id;
        n.requestDecisionedAt = n.requestDecisionedAt ?? n.RequestDecisionedAt ?? null;
        n.requestAt = n.requestAt ?? n.RequestAt ?? n.createdAt ?? n.CreatedAt ?? null;
        n.durationMinutes = n.durationMinutes ?? n.DurationMinutes ?? n?.hallPassType?.hallPassTypeDuration ?? null;

        // convenience mirrors for downstream fallbacks
        n.pathId =
          n.pathId ??
          n?.explicitPath?.pathId ??
          n?.hallPassTypePath?.pathId ??
          n?.PathId;

        n.startLocationId =
          n.startLocationId ??
          n?.startLocation?.locationId ??
          n?.StartLocationId;

        n.destinationLocationId =
          n.destinationLocationId ??
          n?.destinationLocation?.locationId ??
          n?.DestinationLocationId;

        n.destinationLocationName =
          n.destinationLocationName ??
          n?.destinationLocation?.name ??
          n?.DestinationLocationName ??
          n?.destinationName;

        return n;
      })();

      // Forward to local subscribers
      onHallPass?.(normalized);

      // Notify app-wide data hook (map/listeners)
      try {
        window.dispatchEvent(
          new CustomEvent('hallPassUpdated', {
            detail: { type: 'upsert', hallPass: normalized },
          })
        );
      } catch (e) {
        console.warn('Failed to dispatch hallPassUpdated event', e);
      }
    });
    notificationConnection.on("CancelledHallPass", (hallPassId) => {
      if (HP_DEBUG) console.log('[HP][RAW][CancelledHallPass]', hallPassId);
      onHallPass?.({ hallPassId, status: "cancelled" });
    });
    notificationConnection.on('SummonsHallPass', (...args: any[]) => {
      let payload: any;
      if (args.length === 1 && typeof args[0] === "object") {
        const h = args[0];
        payload = {
          ...h,
          status: "summons",
          summonedBy: h.summonedBy ?? h.SummonedBy ?? true,
          summonedTo: h.summonedTo ?? h.SummonedTo,
        };
      } else {
        const [hallPassId, message, notes] = args;
        payload = {
          hallPassId: hallPassId?.toString?.() ?? hallPassId,
          status: "summons",
          message,
          notes,
          summonedBy: true,
        };
      }
      if (HP_DEBUG) console.log('[HP][RAW][SummonsHallPass]', payload);
      validateHallPassPayload('SummonsHallPass', payload);
      onHallPass?.(payload);
    });
    notificationConnection.on("ReturnHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ReturnHallPass]', hp);
      let payload: any = hp;
      if (hp == null) {
        console.warn('[HP][WARN][ReturnHallPass] Received null/undefined payload');
        return;
      }
      if (typeof hp !== 'object') {
        // API inconsistency: server sent just the hallPassId primitive
        payload = { hallPassId: hp, status: 'returned' };
        if (HP_DEBUG) console.log('[HP][COERCE][ReturnHallPass] primitive -> object', payload);
      } else {
        // Ensure hallPassId field exists & status
        if (!('hallPassId' in payload)) {
          const inferred = (payload as any).id || (payload as any).passId;
          if (inferred) payload.hallPassId = inferred;
        }
        if (!payload.status || payload.status === 'return') {
          payload.status = 'returned';
        }
      }
      if (!payload.hallPassId) {
        console.warn('[HP][WARN][ReturnHallPass] Missing hallPassId after normalization:', payload);
      }
      onHallPass?.(payload);
    });
    notificationConnection.on("ExpiredHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ExpiredHallPass]', hp);
      let payload: any = hp;
      if (hp == null) {
        console.warn('[HP][WARN][ExpiredHallPass] Received null/undefined payload');
        return;
      }
      if (typeof hp !== 'object') {
        payload = { hallPassId: hp, status: 'expired' };
        if (HP_DEBUG) console.log('[HP][COERCE][ExpiredHallPass] primitive -> object', payload);
      } else {
        if (!('hallPassId' in payload)) {
          const inferred = (payload as any).id || (payload as any).passId;
          if (inferred) payload.hallPassId = inferred;
        }
        if (!payload.status) payload.status = 'expired';
      }
      if (!payload.hallPassId) {
        console.warn('[HP][WARN][ExpiredHallPass] Missing hallPassId after normalization:', payload);
      }
      onHallPass?.(payload);
    });
  }
  else {
    notificationConnection.on("ApprovedHallPass", (hallPassId, desc) => {
      console.log("ApprovedHallPass", hallPassId, desc);
      const id = hallPassId?.toString?.() ?? hallPassId;
      // Deduplicate against ReceiveHallPass that may also arrive
      if (id && shouldDedup(`approved:${id}`)) return;
      onHallPass?.({ hallPassId: id, status: "approved", desc });
    });
    notificationConnection.on("DeniedHallPass", (hallPassId, desc) => {
      onHallPass?.({ hallPassId, status: "denied", desc });
    });
    notificationConnection.on('SummonsHallPass', (...args: any[]) => {
      let payload: any;
      if (args.length === 1 && typeof args[0] === "object") {
        const h = args[0];
        payload = {
          ...h,
          status: "summons",
          summonedBy: h.summonedBy ?? h.SummonedBy ?? true,
          summonedTo: h.summonedTo ?? h.SummonedTo,
        };
      } else {
        const [hallPassId, message, notes] = args;
        payload = {
          hallPassId: hallPassId?.toString?.() ?? hallPassId,
          status: "summons",
          message,
          notes,
          summonedBy: true,
        };
      }
      if (HP_DEBUG) console.log('[HP][RAW][SummonsHallPass][STUDENT]', payload);
      onHallPass?.(payload);
    });
    // ←–– new: teacher pushes a fresh pass to student
    notificationConnection.on("ReceiveHallPass", (hp: any) => {
      if (HP_DEBUG) console.log('[HP][RAW][ReceiveHallPass][STUDENT]', hp);

      // Filter hall pass notifications to only intended student
      const hallPassStudentId = hp.studentId;

      if (hallPassStudentId && userId) {
        if (hallPassStudentId !== userId) {
          console.log('ReceiveHallPass: notification not for current student, ignoring');
          return;
        }
      } else if (!userId) {
        console.log('ReceiveHallPass: no user ID available, ignoring hall pass notification');
        return;
      }
      // Allow through if hallPassStudentId is undefined for backward compatibility

      const id = hp?.hallPassId?.toString?.() || hp?.id?.toString?.();
      if (id && shouldDedup(`approved:${id}`)) return; // coalesce with ApprovedHallPass

      onHallPass?.({ ...hp, status: "approved", hallPassId: id ?? hp?.hallPassId });
    });

    // Forward any hall pass update as a "HallPassUpdated" event for the student.
    notificationConnection.on("ReturnHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ReturnHallPass][STUDENT]', hp);

      let payload: any = hp;
      if (hp == null) {
        console.warn('[HP][WARN][ReturnHallPass][STUDENT] Received null/undefined payload');
        return;
      }
      if (typeof hp !== 'object') {
        // API inconsistency: server sent just the hallPassId primitive
        payload = { hallPassId: hp, status: 'returned' };
        if (HP_DEBUG) console.log('[HP][COERCE][ReturnHallPass][STUDENT] primitive -> object', payload);
      } else {
        // Ensure hallPassId field exists & status
        if (!('hallPassId' in payload)) {
          const inferred = (payload as any).id || (payload as any).passId;
          if (inferred) payload.hallPassId = inferred;
        }
        if (!payload.status || payload.status === 'return') {
          payload.status = 'returned';
        }
      }

      // Filter by student ID for return notifications
      const hallPassStudentId = payload?.studentId;

      if (hallPassStudentId && userId) {
        if (hallPassStudentId !== userId) {
          console.log('ReturnHallPass: notification not for current student, ignoring');
          return;
        }
      } else if (!userId) {
        console.log('ReturnHallPass: no user ID available, ignoring hall pass notification');
        return;
      }

      if (!payload.hallPassId) {
        console.warn('[HP][WARN][ReturnHallPass][STUDENT] Missing hallPassId after normalization:', payload);
      }

      onHallPass?.(payload);
    });

    notificationConnection.on("ExpiredHallPass", (hp) => {
      if (HP_DEBUG) console.log('[HP][RAW][ExpiredHallPass][STUDENT]', hp);

      let payload: any = hp;
      if (hp == null) {
        console.warn('[HP][WARN][ExpiredHallPass][STUDENT] Received null/undefined payload');
        return;
      }
      if (typeof hp !== 'object') {
        payload = { hallPassId: hp, status: 'expired' };
        if (HP_DEBUG) console.log('[HP][COERCE][ExpiredHallPass][STUDENT] primitive -> object', payload);
      } else {
        if (!('hallPassId' in payload)) {
          const inferred = (payload as any).id || (payload as any).passId;
          if (inferred) payload.hallPassId = inferred;
        }
        if (!payload.status) payload.status = 'expired';
      }

      // Filter by student ID for expired notifications
      const hallPassStudentId = payload?.studentId;

      if (hallPassStudentId && userId) {
        if (hallPassStudentId !== userId) {
          console.log('ExpiredHallPass: notification not for current student, ignoring');
          return;
        }
      } else if (!userId) {
        console.log('ExpiredHallPass: no user ID available, ignoring hall pass notification');
        return;
      }

      if (!payload.hallPassId) {
        console.warn('[HP][WARN][ExpiredHallPass][STUDENT] Missing hallPassId after normalization:', payload);
      }

      onHallPass?.(payload);
    });
  }

  // ★ NEW ➜ order notifications (same for both contexts)
  notificationConnection.on("OrderPostAcornOrder", (orderId: number) => {
    onOrder?.({ type: 'new', orderId });
  });
  notificationConnection.on("OrderCancel", (orderId: number) => {
    onOrder?.({ type: 'cancel', orderId });
  });
  notificationConnection.off("OrderMarkReady");
  notificationConnection.on(
    "OrderMarkReady",
    (orderId: number, pickupCode: string, pickupLocation: string) => {
      onOrder?.({ type: 'ready', orderId, pickupCode, pickupLocation });
    }
  );

  // Subscribe to house points groups (handle string OR array)
  const ids = Array.isArray(housePointsRootOrgId)
    ? housePointsRootOrgId
    : (housePointsRootOrgId ? [housePointsRootOrgId] : []);

  const normalizedIds = ids.filter(Boolean).map(String);

  if (housePointsReplaceExisting && normalizedIds.length) {
    desiredHousePointsOrgIds = new Set(normalizedIds);
    pendingHousePointsOrgIds = new Set(normalizedIds);
    subscribedHousePointsOrgIds.clear();
  } else {
    normalizedIds.forEach(id => {
      if (!desiredHousePointsOrgIds.has(id)) {
        desiredHousePointsOrgIds.add(id);
      }
      if (!subscribedHousePointsOrgIds.has(id)) {
        pendingHousePointsOrgIds.add(id);
      }
    });
  }

  if (notificationConnection.state === signalR.HubConnectionState.Disconnected) {
    try {
      // Starting a new connection means server-side group memberships are empty.
      // Ensure we re-subscribe to all desired groups after start.
      subscribedHousePointsOrgIds.clear();
      Array.from(desiredHousePointsOrgIds).forEach(id => pendingHousePointsOrgIds.add(id));
      await notificationConnection.start();
      // Wait a moment for connection to stabilize before subscribing
      await new Promise(resolve => setTimeout(resolve, 100));
      await flushPendingHousePointsSubs();
    } catch (err) {
      console.error("Error starting notification connection:", err);
    }
  } else if (notificationConnection.state === signalR.HubConnectionState.Connected) {
    // Connection already active, flush immediately
    await flushPendingHousePointsSubs();
  } else {
    // Connection is connecting or reconnecting, wait for it
    const waitForConnection = new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {
        if (notificationConnection?.state === signalR.HubConnectionState.Connected) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 10000);
    });
    await waitForConnection;
    await flushPendingHousePointsSubs();
  }

  // NEW ➜ expose the connection to the caller
  return notificationConnection;
}
